import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  FileText,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { api } from "@/lib/api";

function StatCard({ icon: Icon, label, value, tone = "amber", testId }) {
  const tones = {
    amber: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
    slate: "bg-slate-500/15 text-slate-300 ring-slate-500/30",
    red: "bg-red-500/15 text-red-300 ring-red-500/30",
    emerald: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  };
  return (
    <div
      className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-xl ring-1 ring-inset ${tones[tone]}`}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="mt-4 text-3xl font-semibold tracking-tight text-white tabular-nums">
        {value}
      </div>
      <div className="mt-1 text-xs uppercase tracking-wider text-slate-400">
        {label}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/platform/metrics");
        setData(res.data);
      } catch (err) {
        setError(err?.response?.data?.detail || "Failed to load metrics");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="admin-dashboard-page">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            Platform overview
          </h1>
          <p className="mt-2 max-w-lg text-sm text-slate-400">
            Real-time snapshot of every tenant on InvoicePro.
          </p>
        </div>
      </header>

      <div
        className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200"
        data-testid="admin-changepw-note"
      >
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div>
            <strong className="text-amber-300">Security reminder:</strong> please
            change the seeded super-admin password on first login. Update it via
            your identity provider or by rotating <code>SEED_SUPERADMIN_PASSWORD</code>{" "}
            in the backend environment.
          </div>
        </div>
      </div>

      {loading && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-10 text-center text-sm text-slate-400">
          Loading platform metrics…
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-sm text-red-200">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={Building2}
              label="Tenants total"
              value={data.tenants_total}
              tone="amber"
              testId="admin-metric-tenants-total"
            />
            <StatCard
              icon={ShieldCheck}
              label="Active"
              value={data.tenants_active}
              tone="emerald"
              testId="admin-metric-tenants-active"
            />
            <StatCard
              icon={AlertTriangle}
              label="Suspended"
              value={data.tenants_suspended}
              tone="red"
              testId="admin-metric-tenants-suspended"
            />
            <StatCard
              icon={UsersRound}
              label="Total users"
              value={data.users_total}
              tone="slate"
              testId="admin-metric-users-total"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <StatCard
              icon={FileText}
              label="Invoices total"
              value={data.invoices_total}
              tone="amber"
              testId="admin-metric-invoices-total"
            />
            <StatCard
              icon={FileText}
              label="Invoices — last 30d"
              value={data.invoices_last_30d}
              tone="emerald"
              testId="admin-metric-invoices-30d"
            />
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
              <div className="text-xs uppercase tracking-wider text-slate-400">
                Plan distribution
              </div>
              <div className="mt-3 space-y-2" data-testid="admin-metric-plans">
                {Object.entries(data.plans || {}).map(([plan, count]) => (
                  <div key={plan} className="flex items-center justify-between text-sm">
                    <span className="capitalize text-slate-300">{plan}</span>
                    <span className="rounded-md bg-slate-800 px-2 py-0.5 font-medium tabular-nums text-white">
                      {count}
                    </span>
                  </div>
                ))}
                {Object.keys(data.plans || {}).length === 0 && (
                  <div className="text-sm text-slate-500">No tenants yet.</div>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-300">
                Recent tenants
              </h2>
              <Link
                to="/admin/tenants"
                className="text-xs font-medium text-amber-300 hover:underline"
              >
                View all →
              </Link>
            </div>
            <ul className="divide-y divide-slate-800" data-testid="admin-recent-tenants">
              {(data.recent_tenants || []).map((t) => (
                <li
                  key={t.id}
                  className="flex items-center justify-between gap-3 py-3 text-sm"
                >
                  <Link
                    to={`/admin/tenants/${t.id}`}
                    className="flex-1 truncate font-medium text-white hover:text-amber-300"
                  >
                    {t.name}
                  </Link>
                  <span className="rounded-md bg-slate-800 px-2 py-0.5 text-xs uppercase tracking-wide text-slate-300">
                    {t.plan}
                  </span>
                  <span
                    className={
                      t.is_active
                        ? "rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-300"
                        : "rounded-md bg-red-500/15 px-2 py-0.5 text-xs text-red-300"
                    }
                  >
                    {t.is_active ? "Active" : "Suspended"}
                  </span>
                </li>
              ))}
              {(data.recent_tenants || []).length === 0 && (
                <li className="py-6 text-center text-sm text-slate-500">
                  No tenants yet.
                </li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
